import { create } from 'zustand';
import { mockCoach, mockAthlete, mockTeam, mockAthleteSelf } from '@/mocks';
import type { MockUser, MockTeam, MockAthlete } from '@/mocks/types';

type Role = 'coach' | 'atleta';

const ACCESS_KEY = 'swimsuite_access_token';
const REFRESH_KEY = 'swimsuite_refresh_token';
const EXPIRES_KEY = 'swimsuite_token_expires';

// Mode determinato dal build env: .env.mock -> mock, .env.real -> real
export const APP_MODE: 'mock' | 'real' = import.meta.env.VITE_MODE === 'real' ? 'real' : 'mock';
export const BYPASS_AUTH: boolean = import.meta.env.VITE_BYPASS_AUTH === 'true';

interface AuthState {
    accessToken: string | null;
    refreshToken: string | null;
    role: Role;
    user: MockUser;
    team: MockTeam;
    athleteProfile: MockAthlete;
    isAuthenticated: boolean;
    isLoading: boolean;
    mockMode: boolean;
    bypassAuth: boolean;

    setTokens: (access: string, refresh: string, expiresIn: number) => void;
    clearTokens: () => void;
    setRole: (role: Role) => void;
    toggleRole: () => void;
    setLoading: (loading: boolean) => void;
    initialize: () => Promise<void>;
    updatePhoto: (url: string | null) => void;
    logout: () => void;
}

function initialAuthState() {
    if (BYPASS_AUTH) {
        return {
            accessToken: 'mock-access-token',
            refreshToken: 'mock-refresh-token',
            isAuthenticated: true
        };
    }
    const access = localStorage.getItem(ACCESS_KEY);
    const refresh = localStorage.getItem(REFRESH_KEY);
    const expires = localStorage.getItem(EXPIRES_KEY);
    const hasValidToken = !!(access && refresh && expires && Date.now() < parseInt(expires));
    return {
        accessToken: hasValidToken ? access : null,
        refreshToken: hasValidToken ? refresh : null,
        isAuthenticated: hasValidToken
    };
}

const tokenInitial = initialAuthState();

export const useAuthStore = create<AuthState>((set, get) => ({
    ...tokenInitial,
    role: 'coach',
    user: mockCoach,
    team: mockTeam,
    athleteProfile: mockAthleteSelf,
    isLoading: false,
    mockMode: APP_MODE === 'mock',
    bypassAuth: BYPASS_AUTH,

    setTokens: (access, refresh, expiresIn) => {
        const expires = Date.now() + expiresIn * 1000;
        localStorage.setItem(ACCESS_KEY, access);
        localStorage.setItem(REFRESH_KEY, refresh);
        localStorage.setItem(EXPIRES_KEY, String(expires));
        set({ accessToken: access, refreshToken: refresh, isAuthenticated: true });
    },

    clearTokens: () => {
        localStorage.removeItem(ACCESS_KEY);
        localStorage.removeItem(REFRESH_KEY);
        localStorage.removeItem(EXPIRES_KEY);
        set({ accessToken: null, refreshToken: null, isAuthenticated: false });
    },

    setRole: role => set({ role, user: role === 'coach' ? mockCoach : mockAthlete }),

    toggleRole: () => {
        const newRole: Role = get().role === 'coach' ? 'atleta' : 'coach';
        set({ role: newRole, user: newRole === 'coach' ? mockCoach : mockAthlete });
    },

    setLoading: isLoading => set({ isLoading }),

    updatePhoto: url => set(s => ({ user: { ...s.user, avatar: url ?? undefined } })),

    /**
     * PRD 17 MACRO A.2 — Fix LC-75.
     * Chiama /auth/me se token valido per popolare user/team/athleteProfile reali.
     * Fallback: clear tokens + redirect login se /auth/me fallisce.
     */
    initialize: async () => {
        if (BYPASS_AUTH) {
            set({ isLoading: false, isAuthenticated: true });
            return;
        }
        set({ isLoading: true });
        const access = localStorage.getItem(ACCESS_KEY);
        if (!access) {
            set({ isLoading: false, isAuthenticated: false });
            return;
        }
        try {
            // Lazy import per evitare circular dep api ↔ authStore
            const { apiGet } = await import('@/lib/api');
            interface AuthMeResponse {
                role: string;
                sub: string;
                coach_id?: string;
                user_account_id?: string;
                athlete_id?: string;
                nome?: string;
                cognome?: string;
                email?: string;
                team_id?: string;
                onboarding_complete?: boolean;
                photo_url?: string | null;
            }
            const meResp = await apiGet<AuthMeResponse>('/auth/me', { dsKey: 'auth' });
            if (!meResp.ok || !meResp.data) {
                get().clearTokens();
                set({ isLoading: false });
                return;
            }
            const me = meResp.data;
            const role: Role = me.role === 'coach' ? 'coach' : 'atleta';

            // Update user con dati reali
            const userId = me.coach_id || me.user_account_id || me.sub;
            const fullName = `${me.nome || ''} ${me.cognome || ''}`.trim();

            set({
                role,
                user: {
                    id: userId || '',
                    name: fullName || (role === 'coach' ? mockCoach.name : mockAthlete.name),
                    email: me.email || '',
                    role: role,
                    avatar: me.photo_url || undefined
                } as MockUser,
                isAuthenticated: true
            });

            // Fetch team se team_id presente; altrimenti reset a empty (atleta libero / coach senza team)
            if (me.team_id) {
                try {
                    const teamResp = await apiGet<unknown>('/teams/me', { dsKey: 'teams' });
                    if (teamResp.ok && teamResp.data) {
                        const { mapTeam } = await import('@/lib/mappers');
                        const mapped = mapTeam(teamResp.data as Parameters<typeof mapTeam>[0]);
                        if (mapped) set({ team: mapped });
                    }
                } catch {
                    // Team fetch fallito → fallback empty per evitare mockTeam visibile
                    set({
                        team: {
                            id: '',
                            name: '',
                            code: '',
                            city: '',
                            province: '',
                            region: '',
                            foundedYear: 0
                        } as unknown as MockTeam
                    });
                }
            } else {
                // No team → atleta libero / coach senza team. Reset team a empty per non mostrare mockTeam.
                set({
                    team: {
                        id: '',
                        name: '',
                        code: '',
                        city: '',
                        province: '',
                        region: '',
                        foundedYear: 0
                    } as unknown as MockTeam
                });
            }

            // PRD 24: se atleta, popola athleteProfile reale via /athletes/me (PRD 23 endpoint).
            // Senza questa chiamata athleteProfile resterebbe mockAthleteSelf anche post-login real.
            if (role === 'atleta') {
                try {
                    const athleteResp = await apiGet<unknown>('/athletes/me', { dsKey: 'athletes' });
                    if (athleteResp.ok && athleteResp.data) {
                        const { mapAthlete } = await import('@/lib/mappers');
                        const mapped = mapAthlete(athleteResp.data as Parameters<typeof mapAthlete>[0]);
                        set({ athleteProfile: mapped as unknown as MockAthlete });
                    }
                } catch {
                    // Mantieni mockAthleteSelf come fallback
                }
            }
        } catch {
            get().clearTokens();
        } finally {
            set({ isLoading: false });
        }
    },

    logout: () => {
        // Best-effort backend logout (DB-revoke refresh token)
        const refreshToken = localStorage.getItem(REFRESH_KEY);
        if (refreshToken) {
            // Lazy fire-and-forget, no await
            import('@/lib/api').then(({ apiPost }) => {
                apiPost('/auth/logout', { refresh_token: refreshToken }, { dsKey: 'auth' }).catch(() => {
                    /* server-side fail OK */
                });
            });
        }
        localStorage.removeItem(ACCESS_KEY);
        localStorage.removeItem(REFRESH_KEY);
        localStorage.removeItem(EXPIRES_KEY);
        set({ accessToken: null, refreshToken: null, isAuthenticated: BYPASS_AUTH });
    }
}));
